import { SessionRole } from "~/api/dto/auth/session-role"
import { useAppSelector } from "../app/hooks"
import {
  selectSubscriptionData,
  selectUserSessionRole,
} from "../app/selectors/user.selector"
import { ACL } from "../utils/ACL"
import { CompanyTier } from "../api/types/company.types"
import { useMemo } from "react"
import useCurrentCompany from "./current-company"

export default function useAccess() {
  const sessionRole = useAppSelector(selectUserSessionRole)
  const currentCompany = useCurrentCompany()
  const subscriptionData = useAppSelector(selectSubscriptionData)

  const isSuperAdmin = useMemo(
    () => sessionRole === SessionRole.SUPER_ADMIN,
    [sessionRole],
  )

  const isAccessGranted = useMemo(
    () => ACL.isAccessGranted(currentCompany, subscriptionData),
    [currentCompany],
  )

  const hasAccess = useMemo(
    () => isSuperAdmin || isAccessGranted,
    [isSuperAdmin, isAccessGranted],
  )

  const isBillingEnabled = useMemo(
    () => Boolean(currentCompany?.metadata?.isBillingEnabled),
    [currentCompany],
  )

  // Free Share of Search company viewed by a regular user. ACL grants these
  // companies blanket isAccessGranted (so guards let them into Main at all);
  // this flag is what SubscriptionGuard/nav use to scope that access down to
  // the SoS surface (APP-453). Super admins keep the full app.
  const isSosFree = useMemo(
    () => currentCompany?.tier === CompanyTier.SOS_FREE && !isSuperAdmin,
    [currentCompany, isSuperAdmin],
  )

  // Aged-out non-paying company viewed by a regular user: the Auth routing
  // replaces the whole app with StalePaywall. Super admins keep Main so
  // support can inspect and restore the company.
  const isStale = useMemo(
    () => currentCompany?.tier === CompanyTier.STALE && !isSuperAdmin,
    [currentCompany, isSuperAdmin],
  )

  return {
    hasAccess,
    isSuperAdmin,
    isAccessGranted,
    isBillingEnabled,
    isSosFree,
    isStale,
  }
}
